import { useGetMarketAccountDetailsQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import {
  useAvailableCollateralAssets,
  useFixingIndexMapping,
  useRateSourceMapping,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { isAvailableUnderlyingSymbol } from '@src/utils/OTC/otcUtils';
import { EMPTY_OBJECT, MarketAccountTypeEnum, useCurrenciesContext, useMarketAccountsContext } from '@talos/kyoko';
import { useMemo, type ReactNode } from 'react';
import {
  DealProductBuilderAssetsContext,
  type DealProductBuilderAssetsContextValue,
} from './useDealProductBuilderAssets';

interface DealProductBuilderAssetsProviderProps {
  children: ReactNode;
}

export const DealProductBuilderAssetsProvider = ({ children }: DealProductBuilderAssetsProviderProps) => {
  const { currenciesList } = useCurrenciesContext();
  const availableCollateralAssetsFromFixings = useAvailableCollateralAssets();
  const { marketAccountsList } = useMarketAccountsContext();
  const { data: marketAccountDetailsData } = useGetMarketAccountDetailsQuery(EMPTY_OBJECT);

  const fixingIndexMapping = useFixingIndexMapping();
  const rateSourceMapping = useRateSourceMapping();

  // Extract underlying assets from fixingIndexMapping (e.g., "BTC-USD" -> "BTC")
  const availableUnderlyingAssets = useMemo(() => {
    const underlyingSymbols = new Set<string>();

    // Extract symbols from fixingIndexMapping keys that contain a dash
    fixingIndexMapping.forEach((_, key) => {
      if (key.includes('-')) {
        const underlyingSymbol = key.split('-')[0];
        underlyingSymbols.add(underlyingSymbol);
      }
    });

    // Filter currenciesList to only include symbols that exist in fixingIndexMapping
    // Also apply available-underlying filter for Forward/Option compatibility
    return currenciesList
      .filter(cur => underlyingSymbols.has(cur.Symbol) && isAvailableUnderlyingSymbol(cur.Symbol))
      .map(cur => ({
        label: cur.Symbol,
        value: cur.Symbol,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [currenciesList, fixingIndexMapping]);

  const availableBorrowedAssets = useMemo(() => {
    const borrowedAssets = new Set<string>();

    for (const [asset, sources] of rateSourceMapping) {
      if (sources && sources.length > 0) {
        borrowedAssets.add(asset);
      }
    }

    return currenciesList
      .filter(cur => borrowedAssets.has(cur.Symbol))
      .map(cur => ({
        label: cur.Symbol,
        value: cur.Symbol,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [currenciesList, rateSourceMapping]);

  // Collect collateral assets from OTCDerivatives market accounts
  const otcDerivativesCollateralAssets = useMemo(() => {
    if (!marketAccountDetailsData) {
      return [];
    }

    const collateralAssets = new Set<string>();

    // Filter market accounts by type
    const otcDerivativesAccounts = marketAccountsList.filter(ma => ma.Type === MarketAccountTypeEnum.OTCDerivatives);

    // Extract AcceptedCollateral from each market account
    otcDerivativesAccounts.forEach(marketAccount => {
      const details = marketAccountDetailsData.get(marketAccount.Name);
      const agreement = details?.Agreement;
      const acceptedCollateral = agreement?.AcceptedCollateral;

      if (Array.isArray(acceptedCollateral) && acceptedCollateral.length > 0) {
        acceptedCollateral.forEach(ac => {
          if (ac.Asset) {
            collateralAssets.add(ac.Asset);
          }
        });
      }
    });

    // Filter currenciesList to only include assets that exist in market accounts
    return currenciesList
      .filter(cur => collateralAssets.has(cur.Symbol))
      .map(cur => ({
        label: cur.Symbol,
        value: cur.Symbol,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [marketAccountsList, marketAccountDetailsData, currenciesList]);

  // Collect collateral assets from OTCLending market accounts
  const otcLendingCollateralAssets = useMemo(() => {
    if (!marketAccountDetailsData) {
      return [];
    }

    const collateralAssets = new Set<string>();

    // Filter market accounts by type
    const otcLendingAccounts = marketAccountsList.filter(ma => ma.Type === MarketAccountTypeEnum.OTCLending);

    // Extract AcceptedCollateral from each market account
    otcLendingAccounts.forEach(marketAccount => {
      const details = marketAccountDetailsData.get(marketAccount.Name);
      const agreement = details?.Agreement;
      const acceptedCollateral = agreement?.AcceptedCollateral;

      if (Array.isArray(acceptedCollateral) && acceptedCollateral.length > 0) {
        acceptedCollateral.forEach(ac => {
          if (ac.Asset) {
            collateralAssets.add(ac.Asset);
          }
        });
      }
    });

    // Filter currenciesList to only include assets that exist in market accounts
    return currenciesList
      .filter(cur => collateralAssets.has(cur.Symbol))
      .map(cur => ({
        label: cur.Symbol,
        value: cur.Symbol,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [marketAccountsList, marketAccountDetailsData, currenciesList]);

  // Extract collateral assets from fixingIndexMapping, restricted to assets that exist in OTCLending market accounts
  // Don't normalize USD/USDT/USDC - show them separately if they're in accepted collateral
  const availableCollateralAssets = useMemo(() => {
    const collateralAssetsWithFixings = new Set<string>();

    // Use availableCollateralAssetsFromFixings which already extracts from fixingIndexMapping
    availableCollateralAssetsFromFixings.forEach(asset => {
      collateralAssetsWithFixings.add(asset.value);
    });

    // Create a Set of OTCLending collateral asset symbols for fast lookup
    const otcLendingCollateralSet = new Set(otcLendingCollateralAssets.map(asset => asset.value));

    // Filter currenciesList to only include assets that:
    // 1. Have fixings in fixingIndexMapping AND exist in OTCLending market accounts
    // OR
    // 2. Are USD/USDT/USDC AND exist in OTCLending market accounts (show even without fixings)
    return currenciesList
      .filter(cur => {
        const hasFixings = collateralAssetsWithFixings.has(cur.Symbol);
        const isUsdPegged = ['USD', 'USDT', 'USDC'].includes(cur.Symbol);
        const isInAcceptedCollateral = otcLendingCollateralSet.has(cur.Symbol);
        return isInAcceptedCollateral && (hasFixings || isUsdPegged);
      })
      .map(cur => ({
        label: cur.Symbol,
        value: cur.Symbol,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [currenciesList, availableCollateralAssetsFromFixings, otcLendingCollateralAssets]);
  const contextValue: DealProductBuilderAssetsContextValue = useMemo(
    () => ({
      availableUnderlyingAssets,
      availableBorrowedAssets,
      availableCollateralAssets,
      otcDerivativesCollateralAssets,
      otcLendingCollateralAssets,
    }),
    [
      availableUnderlyingAssets,
      availableBorrowedAssets,
      availableCollateralAssets,
      otcDerivativesCollateralAssets,
      otcLendingCollateralAssets,
    ]
  );

  return (
    <DealProductBuilderAssetsContext.Provider value={contextValue}>{children}</DealProductBuilderAssetsContext.Provider>
  );
};
